/**
 * Mirrors the backend format registry (`src/services/document-formats.ts`): the
 * extensions it accepts, in its order, and how the admin UI shows each one. Both specs
 * assert the same list, so a format added on one side only fails a test.
 */

/** How the viewer's "Original" tab shows a document; `download` offers the file instead of rendering it. */
export type DocumentSourceKind = 'pdf' | 'image' | 'download';

export const DOCUMENT_FORMATS: ReadonlyArray<{ extension: string; sourceKind: DocumentSourceKind; label: string }> = [
  { extension: '.pdf', sourceKind: 'pdf', label: 'PDF' },
  { extension: '.png', sourceKind: 'image', label: 'PNG' },
  { extension: '.jpg', sourceKind: 'image', label: 'JPEG' },
  { extension: '.jpeg', sourceKind: 'image', label: 'JPEG' },
  { extension: '.docx', sourceKind: 'download', label: 'Word' },
  { extension: '.pptx', sourceKind: 'download', label: 'PowerPoint' },
  { extension: '.tif', sourceKind: 'image', label: 'TIFF' },
  { extension: '.tiff', sourceKind: 'image', label: 'TIFF' },
  { extension: '.avif', sourceKind: 'image', label: 'AVIF' },
  { extension: '.webp', sourceKind: 'image', label: 'WebP' },
  { extension: '.gif', sourceKind: 'image', label: 'GIF' },
  { extension: '.xlsx', sourceKind: 'download', label: 'Excel' },
  { extension: '.xlsm', sourceKind: 'download', label: 'Excel' },
  { extension: '.csv', sourceKind: 'download', label: 'CSV' },
  { extension: '.tsv', sourceKind: 'download', label: 'CSV' },
  { extension: '.html', sourceKind: 'download', label: 'HTML' },
  { extension: '.htm', sourceKind: 'download', label: 'HTML' },
  { extension: '.txt', sourceKind: 'download', label: 'Text' },
  { extension: '.md', sourceKind: 'download', label: 'Markdown' },
  { extension: '.json', sourceKind: 'download', label: 'JSON' },
  { extension: '.xml', sourceKind: 'download', label: 'XML' }
];

export const SUPPORTED_EXTENSIONS: readonly string[] = DOCUMENT_FORMATS.map(format => format.extension);

/** Value for a file input's `accept` attribute. */
export const DOCUMENT_ACCEPT = SUPPORTED_EXTENSIONS.join(',');

/** Every supported kind of file with its extensions, e.g. `PDF (.pdf), JPEG (.jpg, .jpeg) … or XML (.xml)`. */
export function supportedFormatsText(): string {
  const extensionsByLabel = new Map<string, string[]>();
  for (const { label, extension } of DOCUMENT_FORMATS) {
    extensionsByLabel.set(label, [...(extensionsByLabel.get(label) ?? []), extension]);
  }
  const kinds = [...extensionsByLabel].map(([label, extensions]) => `${label} (${extensions.join(', ')})`);
  return kinds.length > 1 ? `${kinds.slice(0, -1).join(', ')} or ${kinds[kinds.length - 1]}` : kinds.join('');
}

/** Same shape the backend keeps in a key; anything else is stored as `.pdf`. */
const SAFE_EXTENSION = /^[A-Za-z0-9]{1,10}$/;

/** The extension the backend will store a file under, e.g. `.png`; `.pdf` when the name has no usable one. */
export function uploadExtension(fileName: string): string {
  const candidate = fileName.includes('.') ? fileName.slice(fileName.lastIndexOf('.') + 1) : '';
  return SAFE_EXTENSION.test(candidate) ? `.${candidate.toLowerCase()}` : '.pdf';
}

/** The backend's 400 message for the file, or `null` when the file's format is accepted. */
export function unsupportedFormatMessage(fileName: string): string | null {
  const extension = uploadExtension(fileName);
  return SUPPORTED_EXTENSIONS.includes(extension)
    ? null
    : `Unsupported file format ${extension}. Supported: ${SUPPORTED_EXTENSIONS.join(', ')}`;
}

function formatFor(extension: string) {
  return DOCUMENT_FORMATS.find(format => format.extension === extension);
}

/** Read side, tolerant like the backend: an absent or unknown extension is a PDF. */
export function sourceKindFromKey(s3Key: string | null | undefined): DocumentSourceKind {
  const baseName = (s3Key ?? '').slice((s3Key ?? '').lastIndexOf('/') + 1);
  const dot = baseName.lastIndexOf('.');
  const extension = dot === -1 ? '' : baseName.slice(dot).toLowerCase();
  return formatFor(extension)?.sourceKind ?? 'pdf';
}

/** How the upload dialog previews a file it has accepted. */
export function previewKindForFile(fileName: string): DocumentSourceKind {
  return formatFor(uploadExtension(fileName))?.sourceKind ?? 'pdf';
}
